import { mergeStyles } from "@fluentui/react";
import { Dispatch, useEffect, useReducer } from "react";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import { IPartnerFilterPillDispatchProps, IPartnerReducerType } from "../../interfaces/PartnerView.interfaces";
import { CustomFilterPill } from "../../SharedComponents/CustomFilterPill";
import { useGetFiltersQuery } from "../../slices/Common.slice";
import { mapPartnerFilterPillMenuItemsPredicate, partnerFilterPillReducer } from "./PartnerSearch.common";
import { PARTNER_FILTER_PILL } from "./PartnerSearch.constants";

export const PartnerFilterPill = (props: any) => {
  const [partnerCountryState, partnerCountryDispatch] = useReducer(
    partnerFilterPillReducer,
    PARTNER_FILTER_PILL.INITIAL_STATE
  );
  const [isEliteState, isEliteDispatch] = useReducer(partnerFilterPillReducer, PARTNER_FILTER_PILL.INITIAL_STATE);
  const [isDefaultState, isDefaultDispatch] = useReducer(partnerFilterPillReducer, PARTNER_FILTER_PILL.INITIAL_STATE);
  const [isChannelPartnerState, isChannelPartnerDispatch] = useReducer(
    partnerFilterPillReducer,
    PARTNER_FILTER_PILL.INITIAL_STATE
  );
  const { data, error } = useGetFiltersQuery(null);

  const createUpdateHandler = (dispatch: Dispatch<IPartnerFilterPillDispatchProps>) => (items?: string[]) => {
    dispatch({ type: IPartnerReducerType.UpdateSelectedMenuItems, data: items || [] });
  };

  const onPartnerCountryUpdateHandler = createUpdateHandler(partnerCountryDispatch);
  const onIsEliteUpdateHandler = createUpdateHandler(isEliteDispatch);
  const onIsDefaultUpdateHandler = createUpdateHandler(isDefaultDispatch);
  const onIsChannelPartnerUpdateHandler = createUpdateHandler(isChannelPartnerDispatch);

  useEffect(() => {
    const customProps = {
      partnerSearchFilterPillProps: props
    };
    if (data) {
      partnerCountryDispatch({
        type: IPartnerReducerType.LoadMenuItems,
        data: ((data?.regionNames as string[]) || []).map(mapPartnerFilterPillMenuItemsPredicate)
      });
      isEliteDispatch({ type: IPartnerReducerType.LoadMenuItems, data: PARTNER_FILTER_PILL.INITIAL_FILTER_VALUES });
      isDefaultDispatch({ type: IPartnerReducerType.LoadMenuItems, data: PARTNER_FILTER_PILL.INITIAL_FILTER_VALUES });
      isChannelPartnerDispatch({
        type: IPartnerReducerType.LoadMenuItems,
        data: PARTNER_FILTER_PILL.INITIAL_FILTER_VALUES
      });
      logEvent("useGetFiltersQuery API call success", "Partner Search", "PartnerFilterPill", customProps);
    }
    if (error) {
      logException("Partner Search", "PartnerFilterPill", "useGetFiltersQuery", error, customProps);
    }
  }, [data]);

  useEffect(() => {
    let newPartnerCountryValue: string = "";
    partnerCountryState.selectedMenuItems.forEach((element: string) => {
      newPartnerCountryValue = element + ";" + newPartnerCountryValue;
    });
    let newIsEliteValue: string = "";
    isEliteState.selectedMenuItems.forEach((element: string | string[]) => {
      newIsEliteValue = isEliteState.selectedMenuItems.length > 1 ? "" : element.includes("Yes") ? "1" : "0";
    });
    let newIsDefaultValue: string = "";
    isDefaultState.selectedMenuItems.forEach((element: string | string[]) => {
      newIsDefaultValue = isDefaultState.selectedMenuItems.length > 1 ? "" : element.includes("Yes") ? "1" : "0";
    });
    let newIsChannelPartnerValue: string = "";
    isChannelPartnerState.selectedMenuItems.forEach((element: string | string[]) => {
      newIsChannelPartnerValue =
        isChannelPartnerState.selectedMenuItems.length > 1 ? "" : element.includes("Yes") ? "1" : "0";
    });
    props.onIsDefaultChange(newIsDefaultValue);
    props.onIsEliteChange(newIsEliteValue);
    props.onIsChannelPartnerChange(newIsChannelPartnerValue);
    props.onPartnerCountryChange(newPartnerCountryValue);
  }, [
    partnerCountryState.selectedMenuItems,
    isEliteState.selectedMenuItems,
    isDefaultState.selectedMenuItems,
    isChannelPartnerState.selectedMenuItems
  ]);

  return (
    <div
      className={mergeStyles({
        margin: "-4px",
        display: "flex",
        flexWrap: "wrap"
      })}
    >
      {PARTNER_FILTER_PILL.INITIAL_DATA.map((filterPillProps) => {
        const { key } = filterPillProps;
        const stateMapping: any = {
          partnerCountry: { state: partnerCountryState, handler: onPartnerCountryUpdateHandler },
          isElite: { state: isEliteState, handler: onIsEliteUpdateHandler },
          isDefault: { state: isDefaultState, handler: onIsDefaultUpdateHandler },
          isChannelPartner: { state: isChannelPartnerState, handler: onIsChannelPartnerUpdateHandler }
        };

        if (key !== undefined) {
          const mapping = stateMapping[key];
          filterPillProps.menuProps = {
            focusZoneProps: {
              /* Allow up and down arrows to move focus out of the SearchBox */
              shouldInputLoseFocusOnArrowKey: () => true
            },
            items: mapping.state.menuItems
          };
          filterPillProps.onApply = mapping.handler;
          filterPillProps.onCancel = mapping.handler;
          filterPillProps.onResetFilters = () => mapping.handler([]);
        }

        return <CustomFilterPill {...filterPillProps} key={filterPillProps.key} />;
      })}
    </div>
  );
};
